<template>
  <v-container fluid>
    <notifications group="zeiten" position="top center" />
    <v-toolbar color="white">
      <h2 class="headline ml-5"> <v-icon>mdi-alarm</v-icon> Zeiten</h2>
      <v-spacer></v-spacer>
      <div class="mr-14" style="max-width: 16%">
        <v-select
            hide-details
            :items="projectTypes"
            v-model="selectedProjectType"
            hint="Projekte"
        />
      </div>
      <admin-only>
        <v-btn
            @click="showMetadataDefinitions = true"
            color="blue darken-4"
            dark
            small
            fab
        >
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </admin-only>
    </v-toolbar>
    <user-times-export class="mt-4" :show-max-stunden-tooltip="hasMaxStundenLohnart" />
    <div class="mt-4 d-flex justify-space-between">
      <div>
        <v-btn
            @click="showNewEntryDialog = true"
            color="primary"
            class="mr-2"
            dark
            small
            fab
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
                v-on="on"
                color="blue-grey lighten-3"
                dark
                small
                fab
                @click="clearFilters"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>Filter zurücksetzen</span>
        </v-tooltip>
      </div>

      <admin-only>
        <div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                  v-on="on"
                  @click="downloadZeitenReport"
                  :loading="downloadingReport"
                  class="mr-2"
                  color="blue"
                  dark
                  small
                  fab
              >
                <v-icon>mdi-arrow-down-bold</v-icon>
              </v-btn>
            </template>
            <span>Download Zeiten</span>
          </v-tooltip>
          <v-tooltip top v-if="hasTenantSpecificReport">
            <template v-slot:activator="{ on }">
              <v-btn
                  v-on="on"
                  @click="downloadTenantReport"
                  :loading="downloadingReport"
                  color="warning"
                  dark
                  small
                  fab
              >
                <v-icon>mdi-arrow-down-bold</v-icon>
              </v-btn>
            </template>
            <span>Aufzeichnung pro Mitarbeiter</span>
          </v-tooltip>
        </div>
      </admin-only>
    </div>

    <v-data-table
      :headers="headers"
      :items="data"
      :hide-default-header="false"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalItems"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
        itemsPerPageText: 'Einträge pro Seite'
      }"
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:header="{ props: { headers } }">
        <thead class="v-data-table-header">
          <tr>
            <th
              v-for="(header, hi) in headers"
              :key="header.text"
            >
              <filter-box
                :ref="`filter${hi}`"
                :placeholder="header.filterPlaceholder"
                :initialValue="getFilterInitialValue(header.value)"
                :type="header.filterType"
                :selectItems="header.selectItems"
                :disabled="header.filterDisabled"
                @filter="applyFilter(header.value, ...arguments)"
                v-if="header.filterable"
              />
            </th>
          </tr>
        </thead>
      </template>
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-for="item in items" :key="item.name" @click="selectItemRow(item)" :style="createProjectCustomStyle(item)">
            <td class="text-xs-left">{{ item.user }}</td>
            <td class="text-xs-left">{{ getDatum(item) }}</td>
            <td class="text-xs-left">{{ getStartTime(item) }}</td>
            <td class="text-xs-left">{{ getEndTime(item) }}</td>
            <td class="text-xs-left">{{ getProject(item) }}</td>
            <td class="text-xs-left">
              {{ getMachines(item) }}
              <v-tooltip top v-if="!!item.comments">
                <template v-slot:activator="{ on }">
                  <v-icon color="blue darken-2" v-on="on" style="float: right;">mdi-chat-outline</v-icon>
                </template>
                <span>{{ item.comments }}</span>
              </v-tooltip>
            </td>
            <td class="text-xs-left">
              {{ getAufgabenparkInfo( item )}}
            </td>
          </tr>
          <tr>
            <td class="text-xs-right"><strong>Total:</strong></td>
            <td class="text-xs-left">{{ totalUsage }}</td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <v-dialog v-model="showEditModal" max-width="850px">
      <edit-timepark-project
        v-if="showEditModal"
        :initialData="selectedItem"
        :userLock="newEntriesAllowedOnlyFor !== null"
        @cancel="selectedItem = null"
        @confirm="onTimeparkProjectChange"
        @delete="onTimeparkProjectDelete"
      />
    </v-dialog>
    <v-dialog v-model="showNewEntryDialog" max-width="90%">
      <edit-timepark-project
        v-if="showNewEntryDialog"
        hideDelete
        :initialData="createNewEntry()"
        :userLock="newEntriesAllowedOnlyFor !== null"
        @cancel="showNewEntryDialog = false"
        @confirm="onTimeparkProjectCreate"
      />
    </v-dialog>
    <vue-slideout-panel
      v-model="showMetadataDefinitions"
      :widths="['85%']"
      closeHtml=""
      @close="showMetadataDefinitions=false"
      :styles="[{},{},{}, {display: 'none'}]"
    >
      <time-recording-metadata-definitions />
    </vue-slideout-panel>
  </v-container>
</template>

<script>

import download from 'downloadjs';
import contentDisposition from 'content-disposition';
import moment from 'moment';
import timeparkApi from '../timeparkApi';
import FilterBox from './common/FilterBox';
import util from '../util';
import TimeRecordingMetadataDefinitions from './metadata/TimeRecordingMetadataDefinitions';
import VueSlideoutPanel from 'vue-slideout-panel';
import EditTimeparkProject from './projekte/EditTimeparkProject';
import UserTimesExport from './export/UserTimesExport';
import constants from "@/constants";
import {mapGetters, mapState} from "vuex";

const AllProjects = 'Alle Projekte';
const TimeparkProjects = 'Timepark Projekte';
const NormalPrjects = 'Normale Projekte';

export default {
  name: 'Zeiten',
  props: ['auth'],
  components: {
    FilterBox,
    EditTimeparkProject,
    TimeRecordingMetadataDefinitions,
    VueSlideoutPanel,
    UserTimesExport,
  },
  watch: {
    timeRecordingMetadataDefinitions() {
      this.reloadData();
    },
    selectedProjectType() {
      this.reloadData();
    },
    pagination: {
      handler() {
        this.reloadData();
      },
      deep: true,
    },
  },
  mounted() {
    if(this.currentUserIsAdministrator) {
      this.defaultFilters.users = [];
      this.filters = {...this.defaultFilters};
      this.headers.find(x => x.value === 'users').filterDisabled = false;
    }
    this.reloadData();
  },
  computed: {
    ...mapGetters(['currentUserIsAdministrator']),
    ...mapState(['timeRecordingMetadataDefinitions']),
    newEntriesAllowedOnlyFor() {
      return this.currentUserIsAdministrator ? null : this.auth.getLoggedInEmail();
    },
    hasTenantSpecificReport() {
      return this.$store.state.hasTenantMachineUsageReportGenerator;
    },
    pagination() {
      return {
        sortBy: this.options.sortBy && this.options.sortBy.length > 0
          ? this.options.sortBy[0]
          : null,
        descending: this.options.sortDesc && this.options.sortDesc.length > 0
          ? this.options.sortDesc[0]
          : false,
        page: this.options.page,
        rowsPerPage: this.options.itemsPerPage,
      };
    },
    showEditModal: {
      get() {
        return !!this.selectedItem;
      },
      set(value) {
        if (!value) {
          this.selectedItem = null;
        }
      },
    },
  },
  methods: {
    createTenandReportDownloadError(message) {
      this.$notify({
        group: 'zeiten',
        title: 'Fehler',
        text: `Aufzeichnung pro Mitarbeiter konnte nicht erstellt werden.<br/>${message}`,
        type: 'error'
      });
    },
    async downloadTenantReport() {
      const dateFilter = this.filters && this.filters.datum;

      if(!dateFilter) {
        this.createTenandReportDownloadError('Bitte geben Sie ein Start und End Datum ein');
        return;
      }

      if(!dateFilter.from) {
        this.createTenandReportDownloadError('Bitte geben Sie ein Start Datum ein');
        return;
      }

      if(!dateFilter.to) {
        this.createTenandReportDownloadError('Bitte geben Sie ein End Datum ein');
        return;
      }


      this.downloadingReport = true;
      const response = await timeparkApi.downloadTenantMachineUsageReport(dateFilter.from, dateFilter.to);

      if (response && response.data) {
        const cd = contentDisposition.parse(response.headers['content-disposition']);
        download(response.data, cd.parameters.filename, response.headers['content-type']);
      }

      this.downloadingReport = false;
    },
    createProjectCustomStyle(item) {
      const isTimeparkProject = item && item.product;
      if (isTimeparkProject) {
        return null;
      }

      return 'background-color: #f5f5f5';
    },
    getFilterInitialValue(header) {
      return this.filters[header];
    },
    applyFilter(header, filter) {
      if (this.resetingFilters) {
        return;
      }

      this.filters[header] = filter;
      this.reloadData();
    },
    changeSort(column) {
      if (this.options.sortBy &&
        this.options.sortBy.length > 0 &&
        this.options.sortBy[0] === column) {
          this.options.sortDesc[0] = !this.options.sortDesc[0];
        } else {
          this.options.sortBy = [column];
          this.options.sortDesc = [false];

        }
    },
    async downloadZeitenReport()
    {
      this.downloadingReport = true;

      try {
        const apiResponse = await timeparkApi.downloadProjectsReport(this.pagination, this.filters, this.selectedProjectType);

        download(
          apiResponse.data,
          `Zeiten${moment().format('DD_MM_YYYY_mm_ss')}.xlsx`,
          apiResponse.headers['content-type']);
      } catch (err) {
        window.console.error(err);
      }

      this.downloadingReport = false;
    },
    async reloadData() {
      this.loading = true;
      try {
        const apiResult = await timeparkApi.getProjects(this.pagination, this.filters, this.selectedProjectType);
        this.hasMaxStundenLohnart = (await timeparkApi.getAnyExtraChargeHasMaxStundenLohnart()).data;

        this.data = apiResult.data.lineItems.data.map( x => {
          const item = {
            ...x,
            metadata: util.combineMetadataWithMetadataDefinitions(
              x.metadata,
              this.timeRecordingMetadataDefinitions),
          };
          if(!item.tools)
          {
            item.tools = [];
          }
          return item;
        });

        this.totalItems = apiResult.data.lineItems.count;
        this.totalUsage = apiResult.data.totalUsage;
      } catch(err) {
        window.console.error(err);
      }

      this.loading = false;
    },
    getDatum(project) {
      const start =  moment(project.start).utc();
      const end =  moment(project.end).utc();
      if (start.isSame(end, 'day')) {
        return start.format('DD.MM.YYYY');
      } else {
        return `${start.format('DD.MM.YYYY')} bis ${end.format('DD.MM.YYYY')}`;
      }
    },
    getStartTime(project) {
      return moment(project.start).local().format('HH:mm');
    },
    getEndTime(project) {
      return moment(project.end).local().format('HH:mm');
    },
    getProject(project) {
      if (!project || !project.product) {
        return constants.nonTimeparkTaskName;
      }
      const product = this.$store.state.products.find(x => x.id == project.product);
      const order = product && project.order ? product.orders.find(x => x.id == project.order) : null;
      const activity = order && project.activity ? order.activities.find(x => x.id == project.activity) : null;
      return `${product ? product.name : ''}/${order ? order.name : ''}/${activity ? activity.name : ''}`;
    },
    getMachines(project) {
      if(!project.machine) {
        return '';
      }
      const machine = this.$store.state.machines.find(x => x.id == project.machine);
      if(!machine) {
        return '';
      }
      const machines = [machine.name];
      if(project.tools) {
        const toolNames = project.tools.map(t => {
          const tool = machine.tools.find(x => x.id == t);
          return tool ? tool.name : null;
        }).filter(t => !!t);
        machines.push(...toolNames);
      }

      return machines.join(' / ');
    },
    getAufgabenparkInfo(project) {
      if (!project.aufgabenparkContextNumber) {
        return '';
      }

      return `#${project.aufgabenparkContextNumber} ${project.customName}`;
    },
    selectItemRow(item) {
      this.selectedItem = item;
    },
    async onTimeparkProjectDelete() {
      await timeparkApi.deleteProjectMachineUsage(this.selectedItem.id);
      await this.reloadData();

      this.selectedItem = null;
    },
    async onTimeparkProjectChange(changedProject) {
      const projectMachineUsage = {
        ...changedProject,
        id: this.selectedItem.id,
      }

      await timeparkApi.editProjectMachineUsage(projectMachineUsage);
      this.selectedItem = null;
      await this.reloadData();
    },
    async onTimeparkProjectCreate(data) {
      const newProject = {
        end: data.end,
        start: data.start,
        user: data.user,
        email: data.email,
        metadata: data.metadata,
        machineUsages: [{
          activity: data.activity,
          order: data.order,
          product: data.product,
          end: data.end,
          start: data.start,
          machine: data.machine,
          tools: data.tools,
          customName: data.customName,
        }],
        aufgabenparkContextId: data.aufgabenparkContextId,
        aufgabenparkContextIsActivity: data.aufgabenparkContextIsActivity,
        aufgabenparkContextNumber: data.aufgabenparkContextNumber,
        customName: data.customName,
      };

      await timeparkApi.createProject(newProject);
      this.showNewEntryDialog = false;
      await this.reloadData();
    },
    createNewEntry() {
      return {
        start: null,
        end: null,
        email: this.newEntriesAllowedOnlyFor,
        isTimeparkProject: false,
        project: {
          product: null,
          order: null,
          activity: null,
        },
        machine: null,
        tools: [],
        metadata: util.combineMetadataWithMetadataDefinitions(
          [],
          this.timeRecordingMetadataDefinitions),
      };
    },
    async clearFilters() {
      this.resetingFilters = true;

      this.filters = {...this.defaultFilters};
      await this.$nextTick();

      if (this.$refs['filter0']) { this.$refs['filter0'][0].clear(); }
      if (this.$refs['filter1']) { this.$refs['filter1'][0].clear(); }
      if (this.$refs['filter2']) { this.$refs['filter2'][0].clear(); }
      if (this.$refs['filter3']) { this.$refs['filter3'][0].clear(); }
      if (this.$refs['filter4']) { this.$refs['filter4'][0].clear(); }
      if (this.$refs['filter5']) { this.$refs['filter5'][0].clear(); }

      await this.reloadData();

      this.resetingFilters = false;
    },
  },
  data() {
    return {
      resetingFilters: false,
      defaultFilters: {
        users: [this.auth.getLoggedInName()],
      },
      downloadingReport: false,
      showMetadataDefinitions: false,
      showNewEntryDialog: false,
      loading: false,
      selectedItem: null,
      data: [],
      hasMaxStundenLohnart: false,
      totalItems: 0,
      totalUsage: '',
      options: {
        groupby: [],
        groupByDesc: [],
        itemsPerPage: 100,
        multiSort: false,
        mustSort: false,
        page: 1,
        sortBy: ['datum'],
        sortDesc: [true],
      },
      filters: {
        users: [this.auth.getLoggedInName()],
      },
      selectedProjectType: AllProjects,
      projectTypes: [
        { text: AllProjects },
        { text: NormalPrjects },
        { text: TimeparkProjects },
      ],
      headers: [
        {
          text: 'Bearbeiter',
          align: 'left',
          value: 'users',
          filterable: true,
          sortable: true,
          filterPlaceholder: 'Bearbeiter',
          filterType: 'users',
          filterDisabled: true,
        },
        {
          text: 'Datum',
          align: 'left',
          value: 'datum',
          sortable: true,
          filterable: true,
          filterPlaceholder: 'Datum',
          filterType: 'daterange'
        },
        {
          text: 'Startzeit',
          align: 'left',
          value: 'startzeit',
          sortable: true,
          filterable: false,
        },
        {
          text: 'Endzeit',
          align: 'left',
          value: 'endzeit',
          sortable: false,
          filterable: false,
        },
        {
          text: 'Produkt/Aufgabe/Tätigkeit',
          align: 'left',
          value: 'project',
          filterable: true,
          sortable: false,
          filterPlaceholder: 'Suche...',
          filterType: 'project',
        },
        {
          text: 'Fahrzeug/Geräte',
          align: 'left',
          value: 'machines',
          filterable: true,
          sortable: false,
          filterPlaceholder: 'Suche...',
          filterType: 'machines',
        },
        {
          text: 'Aufgabe',
          align: 'left',
          value: 'aufgabenparkInfo',
          filterable: true,
          sortable: false,
          filterPlaceholder: 'Suche...',
          filterType: 'text',
        },
      ],
    };
  },
};
</script>
